import type { CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import type { Plan, Subscription } from '@/api/types'
import { Badge } from '@/components/ui/Badge'

interface CancelPlanDialogProps {
  open: boolean
  subscription: Subscription
  currentPlan: Plan
  targetPlan: Plan | null  // null = cancel (go to free), Plan = downgrade to this plan
  freePlan: Plan | null
  onConfirm: () => void
  onClose: () => void
  loading?: boolean
}

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,.72)',
  backdropFilter: 'blur(6px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1400,
  padding: 16,
}

const cardStyle: CSSProperties = {
  background: 'linear-gradient(180deg, rgba(10,22,44,.98) 0%, rgba(4,10,24,1) 100%)',
  border: '1px solid rgba(239,68,68,.32)',
  borderRadius: 24,
  padding: '36px 32px 28px',
  maxWidth: 520,
  width: '100%',
  color: '#F8FBFF',
  fontFamily: 'system-ui, sans-serif',
  boxShadow: '0 0 0 1px rgba(239,68,68,.12), 0 32px 80px rgba(0,0,0,.6)',
}

const warningIconStyle: CSSProperties = {
  width: 56,
  height: 56,
  borderRadius: 16,
  background: 'linear-gradient(135deg, rgba(239,68,68,.18) 0%, rgba(220,38,38,.08) 100%)',
  border: '1px solid rgba(239,68,68,.28)',
  display: 'grid',
  placeItems: 'center',
  marginBottom: 20,
  color: '#F87171',
}

function WarningIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
      <path
        d="M14 3L26 24H2L14 3Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path d="M14 11V16" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
      <circle cx="14" cy="20" r="1.2" fill="currentColor" />
    </svg>
  )
}

function LossBullet({ text }: { text: string }) {
  return (
    <li
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        fontSize: 13.5,
        lineHeight: 1.5,
        color: 'rgba(232,240,255,.82)',
      }}
    >
      <span
        style={{
          marginTop: 3,
          flexShrink: 0,
          width: 16,
          height: 16,
          borderRadius: 999,
          background: 'rgba(239,68,68,.18)',
          border: '1px solid rgba(239,68,68,.3)',
          display: 'grid',
          placeItems: 'center',
          color: '#F87171',
        }}
      >
        <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
          <path d="M1.5 1.5L6.5 6.5M6.5 1.5L1.5 6.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </span>
      <span>{text}</span>
    </li>
  )
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  })
}

function toAccessEndDate(dateStr: string | null | undefined): string | null {
  if (!dateStr) return null
  const accessEndDate = new Date(dateStr)
  accessEndDate.setDate(accessEndDate.getDate() - 1)
  return accessEndDate.toISOString()
}

function buildLossItems(
  currentPlan: Plan,
  targetPlan: Plan | null,
  freePlan: Plan | null,
  t: ReturnType<typeof useTranslation>['t'],
): string[] {
  const items: string[] = []
  const effectivePlan = targetPlan ?? freePlan
  const isFullCancel = targetPlan === null
  const isTeam = currentPlan.code.toLowerCase() === 'team'
  const losesPrioritySupport = isTeam && (isFullCancel || targetPlan?.code.toLowerCase() !== 'team')
  const maxEditable = effectivePlan?.max_projects ?? 3

  if (isTeam) {
    items.push(
      t(
        'billing.cancel.lossMembers',
        'Todos os membros do workspace serão removidos ao final do período. Apenas o owner com o login mais recente permanecerá.',
      ),
    )
  }

  if (isFullCancel || (targetPlan && (targetPlan.max_projects ?? 999) < (currentPlan.max_projects ?? 999))) {
    items.push(
      t(
        'billing.cancel.lossProjectsViewOnly',
        'Somente os {n} projetos editados mais recentemente poderão ser modificados. Os demais ficarão em modo de visualização — mas poderão ser movidos para outro workspace.',
        { n: maxEditable },
      ),
    )
  }

  if (isFullCancel || (targetPlan && !targetPlan.allow_export_import)) {
    items.push(
      t(
        'billing.cancel.lossExportImport',
        'Exportação (SQL, dicionário, relatório PDF) e importação de SQL ficarão indisponíveis.',
      ),
    )
  }

  if (isFullCancel || (targetPlan && !targetPlan.allow_version_history)) {
    items.push(
      t(
        'billing.cancel.lossVersionHistory',
        'O histórico de versões dos projetos ficará inacessível.',
      ),
    )
  }

  if (losesPrioritySupport) {
    items.push(
      t(
        'billing.cancel.lossPrioritySupport',
        'O suporte prioritário deixará de estar disponível.',
      ),
    )
  }

  return items
}

export function CancelPlanDialog({
  open,
  subscription,
  currentPlan,
  targetPlan,
  freePlan,
  onConfirm,
  onClose,
  loading = false,
}: CancelPlanDialogProps) {
  const { t } = useTranslation()

  if (!open) return null

  const isCancel = targetPlan === null
  const targetName = targetPlan?.name ?? freePlan?.name ?? 'Free'
  const lossItems = buildLossItems(currentPlan, targetPlan, freePlan, t)

  const title = isCancel
    ? t('billing.cancel.titleCancel')
    : t('billing.cancel.titleDowngrade', { plan: targetName })

  const subtitle = isCancel
    ? t(
        'billing.cancel.subtitleCancel',
        { plan: currentPlan.name },
      )
    : t(
        'billing.cancel.subtitleDowngrade',
        { from: currentPlan.name, to: targetName },
      )

  const confirmLabel = isCancel
    ? t('billing.cancel.confirmCancel')
    : t('billing.cancel.confirmDowngrade')

  const activeUntil = toAccessEndDate(subscription.current_period_end)

  return (
    <div style={overlayStyle}>
      <div style={cardStyle}>
        <div style={warningIconStyle}>
          <WarningIcon />
        </div>

        <h2 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 700, lineHeight: 1.2 }}>
          {title}
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: 'rgba(201,219,255,.6)', fontWeight: 600 }}>
            {isCancel
              ? t('billing.currentBadge')
              : t('billing.cancel.targetPlanLabel')}
          </span>
          <Badge label={isCancel ? currentPlan.name : targetName} tone="neutral" />
        </div>
        <p style={{ margin: '0 0 20px', fontSize: 14, color: 'rgba(201,219,255,.65)', lineHeight: 1.5 }}>
          {subtitle}
        </p>

        {lossItems.length > 0 ? (
          <ul
            style={{
              listStyle: 'none',
              margin: '0 0 24px',
              padding: '16px 18px',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              background: 'rgba(239,68,68,.06)',
              border: '1px solid rgba(239,68,68,.16)',
              borderRadius: 14,
            }}
          >
            {lossItems.map((item) => (
              <LossBullet key={item} text={item} />
            ))}
          </ul>
        ) : null}

        {isCancel && activeUntil ? (
          <div
            style={{
              padding: '12px 16px',
              background: 'rgba(34,211,238,.06)',
              border: '1px solid rgba(34,211,238,.18)',
              borderRadius: 12,
              fontSize: 13,
              color: 'rgba(201,219,255,.72)',
              marginBottom: 24,
              lineHeight: 1.5,
            }}
          >
            {t(
              'billing.cancel.activeUntil',
              { date: formatDate(activeUntil) },
            )}
          </div>
        ) : null}

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              flex: 1,
              height: 46,
              borderRadius: 12,
              border: '1px solid rgba(123,150,255,.28)',
              background: 'transparent',
              color: 'rgba(201,219,255,.8)',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {t('common.cancel', 'Cancelar')}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            style={{
              flex: 1,
              height: 46,
              borderRadius: 12,
              border: 'none',
              background: loading
                ? 'rgba(239,68,68,.3)'
                : 'linear-gradient(90deg, #DC2626 0%, #EF4444 100%)',
              color: '#fff',
              fontSize: 14,
              fontWeight: 700,
              cursor: loading ? 'default' : 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? t('common.loading', 'Aguarde...') : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
